import { useEffect, useState } from 'react';
import type { Job } from '@reelmachine/core';
import { Icon } from '../../ui/Icon';

/**
 * The four phases a person sees. `match` maps the pipeline's step text (services/pipeline/src/logic/script.ts) to a
 * phase. Order of the checks matters: "Testing the concepts" is phase 3 although it mentions concepts, and
 * "Analysing the source" is phase 2 although it mentions the source.
 */
export const SCRIPT_STEPS = [
  { label: 'Read source', hint: 'Reading the press release' },
  { label: 'Find hooks and storylines', hint: 'Facts, core message, three ways to tell it' },
  { label: 'Test against your channel', hint: 'A panel of viewers compares the concepts' },
  { label: 'Write script', hint: 'Lines, on-screen text and visuals' },
];
const PHASE_RULES: Array<[RegExp, number]> = [
  [/test|panel|viewer/i, 2],
  [/writing the script|save|script written/i, 3],
  [/analy|condens|choos|hook|concept|narrative|profile/i, 1],
  [/read|fetch|source/i, 0],
];

/** Index of the running phase: by the pipeline's step text, else estimated from progress. */
export function currentStep(job: Pick<Job, 'step' | 'progress'> | undefined): number {
  const step = job?.step ?? '';
  const exact = SCRIPT_STEPS.findIndex((s) => s.label.toLowerCase() === step.toLowerCase());
  if (exact >= 0) return exact;
  const rule = PHASE_RULES.find(([re]) => re.test(step));
  if (rule) return rule[1];
  return Math.min(SCRIPT_STEPS.length - 1, Math.floor((job?.progress ?? 0) * SCRIPT_STEPS.length));
}

export function formatElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Re-renders every second while mounted. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export function GeneratingView({ job }: { job: Job | undefined }) {
  const cur = currentStep(job);
  const now = useNow();
  const started = job?.startedAt ? Date.parse(job.startedAt) : NaN;
  const elapsed = Number.isFinite(started) ? now - started : undefined;
  // Phase share plus the pipeline's own progress inside the run, so the bar never stands still at a phase start.
  const pct = Math.round(Math.max((cur + 0.5) / SCRIPT_STEPS.length, Math.min(job?.progress ?? 0, 0.98)) * 100);
  return (
    <div className="card gen" role="status" aria-label="Writing script">
      <div className="gen-head">
        <span>
          Step {cur + 1} of {SCRIPT_STEPS.length}
        </span>
        {elapsed !== undefined && <span className="gen-time">{formatElapsed(elapsed)}</span>}
      </div>
      <div className="track" aria-hidden>
        <i style={{ width: `${pct}%` }} />
      </div>
      <ol className="pipe">
        {SCRIPT_STEPS.map((s, i) => (
          <li
            key={s.label}
            className={i < cur ? 'ok' : i === cur ? 'on' : undefined}
            aria-current={i === cur ? 'step' : undefined}
          >
            <b>{i < cur ? <Icon name="check" /> : i === cur ? <span className="spin" aria-hidden /> : i + 1}</b>
            <span>
              {s.label}
              {i === cur && (
                <small>{job?.step && job.step.toLowerCase() !== s.label.toLowerCase() ? job.step : s.hint}</small>
              )}
            </span>
          </li>
        ))}
      </ol>
      <p className="gen-note">
        Usually takes 2 to 4 minutes. You can leave this page, the script keeps being written and shows up in your
        reels.
      </p>
    </div>
  );
}
